import { createContext, use, type ReactNode } from 'react';
import type { ObjectType } from '@/services/db/object-type/entity';
import type { SqlImport } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';

/** Where each tab of the detail lives, as hrefs. */
export type SqlImportTabHrefs = {
  imports: string;
  query: string;
  relationshipsQuery: string;
  settings: string;
};

/** What the detail's chrome resolved once, for the tab rendered inside it. */
export type SqlImportDetailValue = {
  workspaceId: string;
  type: SqlDatabaseType;
  sqlImportId: string;
  sqlImport: SqlImport;
  objectTypes: readonly ObjectType[];
  /** "People" or the object type's name; see `sqlImportSyncTarget`. */
  syncTarget: string;
  /** The segment the import writes to, when it targets an existing one and it still exists. */
  segment: { id: string; name: string } | null;
  canEdit: boolean;
  canDelete: boolean;
  canIdentifyByEmail: boolean;
  hrefs: SqlImportTabHrefs;
  /** Whether the import was deleted from this page; read live, so leave guards stand down. */
  isDeleted: () => boolean;
  /** The import is gone: the tabs' unsaved edits no longer matter, and the page is leaving. */
  markDeleted: () => void;
};

const SqlImportDetailContext = createContext<SqlImportDetailValue | null>(null);

type SqlImportDetailProviderProps = {
  value: SqlImportDetailValue;
  children: ReactNode;
};

export function SqlImportDetailProvider({ value, children }: SqlImportDetailProviderProps) {
  return <SqlImportDetailContext value={value}>{children}</SqlImportDetailContext>;
}

/** The import the detail is showing; tabs render inside the detail, so it is always there. */
export function useSqlImportDetail(): SqlImportDetailValue {
  const value = use(SqlImportDetailContext);
  if (value == null) {
    throw new Error('useSqlImportDetail must be used within the SQL import detail');
  }
  return value;
}
